import { useTranslation } from 'react-i18next';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { selectThesisItems } from '@/utils/doctor-substitute';
import AddressForm from '../addess/address-form';
import { AvatarForm } from '../avatar/avatar-form';
import { DatePickerForm } from '../datepicker/datepicker-form';
import { selectUserInitials } from '../edit-admin-profile/edit-admin-profile-form';
import { RhythmFieldsForm } from '../rhythm-fields';
import { SignatureForm } from '../signature/signature-form';
import { EditUserProfileUseForm } from './use-edit-user-profile-form';

type Props = {
  form: EditUserProfileUseForm;
};

const EditUserProfileForm = ({ form }: Props) => {
  const { t } = useTranslation();
  const { picture, firstname, lastname } = form.getValues();

  const { initials } = selectUserInitials({ firstname, lastname });
  const signatureUrl = form.watch('signatureUrl');

  return (
    <Form {...form}>
      <form className="grid gap-4">
        <AvatarForm
          form={form}
          fieldName="file"
          currentAvatar={picture}
          placeholder={initials}
          onDeleteAvatar={() => {
            form.setValue('picture', null);
          }}
        />
        <FormField
          control={form.control}
          name="lastname"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('edit-user-profile-form.lastname.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder=""
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="firstname"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('edit-user-profile-form.firstname.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder=""
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('edit-user-profile-form.phone-number.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="tel"
                  placeholder={t(
                    'edit-user-profile-form.phone-number.placeholder',
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <DatePickerForm
          form={form}
          fieldName="dateOfBirth"
          label={t('edit-user-profile-form.date-of-birth.label')}
          captionLayout="dropdown"
          disabled={(date: Date) =>
            date > new Date() || date < new Date('1900-01-01')
          }
          defaultMonth={new Date(1990, 0)}
          endMonth={new Date(new Date().getFullYear() - 18, 0)}
        />
        <AddressForm form={form as any} />
        <FormField
          control={form.control}
          name="orderRegistrationNumber"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('edit-user-profile-form.order-registration-number.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder=""
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="rppsNumber"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>
                {t('edit-user-profile-form.rpps-number.label')}
              </FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder=""
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="iban"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>{t('edit-user-profile-form.iban.label')}</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder=""
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="bic"
          render={({ field }) => (
            <FormItem className="grid gap-2">
              <FormLabel>{t('edit-user-profile-form.bic.label')}</FormLabel>
              <FormControl>
                <Input
                  type="text"
                  placeholder=""
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="thesis"
          render={({ field }) => (
            <FormItem>
              <div className="flex flex-row items-center justify-between gap-2 space-y-0">
                <FormLabel>
                  {t('edit-user-profile-form.thesis.label')}
                </FormLabel>

                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>

                  <SelectContent side="top">
                    {selectThesisItems.map((item) => (
                      <SelectItem
                        key={item.id}
                        value={item.value}
                      >
                        {t(item.label)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <FormMessage />
            </FormItem>
          )}
        />
        <RhythmFieldsForm form={form} />
        <SignatureForm
          form={form}
          fieldName="signatureFile"
          label={t('edit-user-profile-form.signature.label')}
          currentSignature={signatureUrl}
        />
      </form>
    </Form>
  );
};

export default EditUserProfileForm;
